/** @format */

import { api } from '../../../../api.ts';
import { store } from '../../../../redux/_store';
import {
  fetchServiceTypes,
  fetchDomains,
  fetchPlanningConfig,
  fetchTripPriorities,
  normalizePlanningList,
} from './planningApi';

const STORAGE_KEY_PREFIX = 'planning_reference_data_v6_';
const LEGACY_STORAGE_KEYS = [
  'planning_reference_data_v5',
  'planning_reference_data_v4',
  'planning_reference_data_v3',
];
const TRIP_PRIORITIES_STORAGE_KEY = 'planning_trip_priorities_v1';

let memoryCache = null;
let memoryCacheKey = null;
let tripPrioritiesMemory = null;

/** The planning config (start hours) belongs to the user's company; users without one plan for Libow. */
function storageKey() {
  const companyId = store.getState().auth?.user?.company?.id;
  return `${STORAGE_KEY_PREFIX}${companyId ?? 'libow'}`;
}

/** Start of the next local calendar day (cache valid until then). */
export function nextLocalDayExpiryTimestamp() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function isFresh(entry) {
  if (!entry || typeof entry.expiresAt !== 'number') {
    return false;
  }
  return Date.now() < entry.expiresAt;
}

function readFromStorage(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!isFresh(parsed)) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

/** @returns {PlanningReferenceCache | null} */
export function getPlanningReferenceCache() {
  const key = storageKey();
  if (memoryCache && memoryCacheKey === key && isFresh(memoryCache)) {
    return memoryCache;
  }
  const stored = readFromStorage(key);
  if (stored) {
    memoryCache = stored;
    memoryCacheKey = key;
    return stored;
  }
  memoryCache = null;
  memoryCacheKey = null;
  return null;
}

/**
 * @param {PlanningReferenceCache} data
 * @param {{ renewExpiry?: boolean }} [options] Pass renewExpiry when storing a fresh API fetch.
 */
function persistTripPriorities(tripPriorities) {
  if (!Array.isArray(tripPriorities) || tripPriorities.length === 0) return;
  tripPrioritiesMemory = tripPriorities;
  try {
    localStorage.setItem(
      TRIP_PRIORITIES_STORAGE_KEY,
      JSON.stringify({ fetchedAt: Date.now(), tripPriorities }),
    );
  } catch (err) {
    console.warn('tripPrioritiesCache: could not persist', err);
  }
}

export function setPlanningReferenceCache(data, { renewExpiry = false } = {}) {
  const now = Date.now();
  const payload = {
    ...data,
    fetchedAt: renewExpiry ? now : data.fetchedAt ?? now,
    expiresAt: renewExpiry
      ? nextLocalDayExpiryTimestamp()
      : data.expiresAt ?? nextLocalDayExpiryTimestamp(),
  };
  const key = storageKey();
  memoryCache = payload;
  memoryCacheKey = key;
  try {
    localStorage.setItem(key, JSON.stringify(payload));
  } catch (err) {
    console.warn('planningReferenceCache: could not persist', err);
  }
  persistTripPriorities(payload.tripPriorities);
}

function readTripPrioritiesFromStorage() {
  try {
    const raw = localStorage.getItem(TRIP_PRIORITIES_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const rows = parsed?.tripPriorities;
    if (!Array.isArray(rows) || rows.length === 0) return null;
    return rows;
  } catch {
    localStorage.removeItem(TRIP_PRIORITIES_STORAGE_KEY);
    return null;
  }
}

export function invalidatePlanningReferenceCache() {
  memoryCache = null;
  memoryCacheKey = null;
  tripPrioritiesMemory = null;
  try {
    localStorage.removeItem(storageKey());
    localStorage.removeItem(TRIP_PRIORITIES_STORAGE_KEY);
    LEGACY_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}

export function patchPlanningReferenceConfig(planningConfig) {
  const current = getPlanningReferenceCache();
  if (!current) return;
  setPlanningReferenceCache({ ...current, planningConfig });
}

/** Trip priority catalog rows (colors). Survives the daily catalog cache expiry. */
export function getTripPrioritiesFromCache() {
  if (Array.isArray(tripPrioritiesMemory) && tripPrioritiesMemory.length) {
    return tripPrioritiesMemory;
  }
  const stored = readTripPrioritiesFromStorage();
  if (stored) {
    tripPrioritiesMemory = stored;
    return stored;
  }
  const fromCatalog = getPlanningReferenceCache()?.tripPriorities;
  if (Array.isArray(fromCatalog) && fromCatalog.length) {
    persistTripPriorities(fromCatalog);
    return fromCatalog;
  }
  return null;
}

/** Cached trip priorities, or a one-time API fetch persisted to localStorage. */
export async function loadTripPriorities() {
  const cached = getTripPrioritiesFromCache();
  if (cached?.length) return cached;
  const rows = await fetchTripPriorities();
  persistTripPriorities(rows);
  return rows;
}

/**
 * Fetches planning catalog data from the API (task templates, domains, config).
 * Technicians are always loaded separately — they are not cached in localStorage.
 */
export async function fetchPlanningReferenceDataFromApi() {
  const [
    taskTemplatesResponse,
    domains,
    serviceTypes,
    planningConfig,
    tripPriorities,
  ] = await Promise.all([
    api.get('/planning/task-templates/'),
    fetchDomains(),
    fetchServiceTypes(),
    fetchPlanningConfig(),
    loadTripPriorities(),
  ]);

  return {
    fetchedAt: Date.now(),
    taskTemplates: normalizePlanningList(taskTemplatesResponse.data),
    competences: [],
    roles: [],
    domains,
    serviceTypes,
    planningConfig,
    tripPriorities,
  };
}

/**
 * @typedef {object} PlanningReferenceCache
 * @property {number} fetchedAt When the catalog was last loaded from the API.
 * @property {number} expiresAt Cache is valid until this timestamp (start of next local day after fetch).
 * @property {object[]} taskTemplates
 * @property {object[]} competences
 * @property {object[]} roles
 * @property {object[]} domains
 * @property {object[]} serviceTypes
 * @property {object} planningConfig
 * @property {object[]} tripPriorities Also stored separately (no daily expiry).
 */
